// WOPI editor host page (ADR 0028 §6 + WOPI Quirk #4 fix).
//
// Why this route exists: the original implementation opened the
// editor's launch URL directly in a new tab via window.open, with
// no Sheafworks-controlled host page. That meant:
//
//   1. Browser tab refresh just reloaded the editor's URL (with
//      the original, possibly-expired token in the query string).
//      No fresh launch, no new token.
//   2. Tokens expire (SHEAFWORKS_WOPI_TOKEN_TTL_SECONDS, 1 hour by
//      default); after that every WOPI call from the editor is 401. The editor surfaces
//      "Unauthorized WOPI host. Please try again later" — looks like
//      a server bug; actually the host's job to refresh.
//
// This route puts a Sheafworks page in the loop: it fetches a fresh
// launch on every mount (so tab refresh = fresh token) and schedules
// a transparent token refresh at half the remaining TTL (so a long-running
// editor session never hits 401 from token expiry).
//
// Per-editor refresh delivery:
//
//   - Collabora: postMessage with shape ``{MessageId:
//     "Action_RefreshAccessToken", Values: {newToken: ...}}`` to
//     the editor iframe's contentWindow. Documented in the
//     Collabora SDK postmessage_api docs. Transparent — editor
//     swaps the token in-place without losing any state.
//
//   - OnlyOffice: best-effort parallel shape (the same MessageId,
//     same Values shape). OnlyOffice's WOPI postMessage API isn't
//     as well documented as Collabora's; this is the consistency-
//     invariants pattern's "wire it, let real-editor testing
//     surface the right shape if this guess is wrong" approach.
//     If real-editor testing of OnlyOffice surfaces a different
//     message shape, refine ``postRefreshTo`` (src/lib/wopi.ts) then.

import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type WopiLaunchPayload,
  launchInOffice,
  refreshWopiToken,
} from "@/api/fileOps";
import { AuthGate } from "@/components/AuthGate";
import {
  editorOriginOf,
  getRefreshFraction,
  postEditorMessage,
  postRefreshTo,
  refreshRetryDelayMs,
} from "@/lib/wopi";

type RefreshReason = "scheduled" | "retry";

interface LaunchState {
  editor: WopiLaunchPayload["editor"];
  editorUrl: string;
  // OnlyOffice launches by FormPost (WOPI Quirk #8): ``editorUrl`` is
  // the form action and the token goes in the POST body. Collabora's
  // ``editorUrl`` is navigable directly. Either way ``editorUrl``
  // yields the editor origin for postMessage matching.
  formPost: boolean;
  accessToken: string;
  // Absolute Unix epoch ms when ``accessToken`` expires. Refresh
  // scheduler computes its delay from ``(expiresAtMs - Date.now())``,
  // so this propagates the WOPI-spec semantics from the API
  // surface down to the timer (see WOPI Quirk #6).
  expiresAtMs: number;
}

const EDITOR_FRAME_NAME = "sheafworks-wopi-editor";

export const Route = createFileRoute("/edit/$fileId")({
  component: function EditPage() {
    return (
      <AuthGate>
        <EditContent />
      </AuthGate>
    );
  },
});

function EditContent(): React.ReactElement {
  const { fileId } = Route.useParams();
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  // editorUrl the form was last submitted for — guards StrictMode's
  // double effect run and token refreshes from re-posting (which would
  // reload the editor).
  const submittedForRef = useRef<string | null>(null);
  const [launch, setLaunch] = useState<LaunchState | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Editor readiness gate (WOPI Quirk #5). Collabora ignores all
  // host messages — including ``Reset_Access_Token`` — until the
  // host has acknowledged the editor's ``App_LoadingStatus:
  // Document_Loaded`` event by sending ``Host_PostmessageReady``.
  // This ref tracks whether the handshake has completed for the
  // current iframe; the refresh effect waits on it (with a generous
  // timeout fallback) before posting the new token.
  const editorReadyRef = useRef(false);
  // Launch state mirrored into a ref so ``triggerRefresh`` can read
  // the current value without being torn down + re-bound on every
  // state update. Stable callback identity matters because future
  // trigger sources (an explicit "Refresh now" button, a different
  // editor's equivalent of an actionable idle signal) can be wired
  // in without restructuring.
  const launchRef = useRef<LaunchState | null>(null);
  launchRef.current = launch;
  // Defensive dedup. Today the only refresh trigger is the
  // proactive timer (one in-flight at a time by construction), so
  // this never fires. Kept against the day a second trigger
  // source is added — the small overhead is worth not having to
  // remember the dedup needs to be re-introduced.
  const refreshInFlightRef = useRef(false);
  // Backoff retry after a failed refresh. Without it a single
  // transient failure (network blip, API restart) left the editor on
  // a token that then expired — the next save 401'd.
  const retryAttemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRefreshRef = useRef<(reason: RefreshReason) => Promise<void>>(
    async () => {},
  );

  // Single refresh path. Stable across renders (empty dep array +
  // ref-based reads), so any future trigger source can call it
  // without re-binding.
  const triggerRefresh = useCallback(
    async (reason: RefreshReason): Promise<void> => {
      const current = launchRef.current;
      if (current === null) return;
      if (refreshInFlightRef.current) {
        console.info("[wopi:refresh]", reason, "skipped (already in flight)");
        return;
      }
      refreshInFlightRef.current = true;
      try {
        const r = await refreshWopiToken(current.accessToken).catch(
          (e: unknown) =>
            ({
              kind: "error",
              status: 0,
              message: e instanceof Error ? e.message : String(e),
            }) as const,
        );
        if (r.kind !== "ok") {
          // Keep the editor open and retry with exponential backoff
          // while the current token is still valid. 401/403/404 are
          // final (session gone, access revoked, file deleted):
          // retrying can't help, and the editor will surface its
          // own error on the next WOPI call.
          const attempt = retryAttemptRef.current;
          const delay = refreshRetryDelayMs(attempt);
          const final =
            r.status === 401 || r.status === 403 || r.status === 404;
          if (!final && Date.now() + delay < current.expiresAtMs) {
            retryAttemptRef.current = attempt + 1;
            console.warn(
              "[wopi:refresh]",
              reason,
              "failed",
              r.message,
              "- retrying in",
              delay,
              "ms",
            );
            retryTimerRef.current = setTimeout(() => {
              retryTimerRef.current = null;
              void triggerRefreshRef.current("retry");
            }, delay);
          } else {
            console.warn(
              "[wopi:refresh]",
              reason,
              "failed",
              r.message,
              "- giving up",
            );
          }
          return;
        }
        retryAttemptRef.current = 0;
        console.info("[wopi:refresh]", reason, "succeeded");
        if (!editorReadyRef.current) {
          // The editor never sent App_LoadingStatus:
          // Document_Loaded — usually means CheckFileInfo's
          // PostMessageOrigin field is missing or wrong (the
          // backend gates emission on it). The token would go
          // out but the editor wouldn't apply it; surface the
          // diagnosis to console rather than silently failing.
          console.warn(
            "[wopi:refresh] editor never signalled ready; " +
              "refresh delivered but may be ignored",
          );
        }
        postRefreshTo(
          iframeRef.current,
          editorOriginOf(current.editorUrl),
          current.editor,
          r.value.access_token,
        );
        setLaunch((prev) =>
          prev === null
            ? null
            : {
                ...prev,
                accessToken: r.value.access_token,
                expiresAtMs: r.value.access_token_expires_at_ms,
              },
        );
      } finally {
        refreshInFlightRef.current = false;
      }
    },
    [],
  );
  triggerRefreshRef.current = triggerRefresh;

  // Cancel a pending retry when the route unmounts.
  useEffect(
    () => () => {
      if (retryTimerRef.current !== null) clearTimeout(retryTimerRef.current);
    },
    [],
  );

  // Mount: fetch a fresh launch URL. Re-runs on fileId change
  // (rare in this route, but keeps the dependency honest).
  useEffect(() => {
    let cancelled = false;
    setError(null);
    setLaunch(null);
    editorReadyRef.current = false;
    retryAttemptRef.current = 0;
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    void launchInOffice(fileId).then((r) => {
      if (cancelled) return;
      if (r.kind === "error") {
        setError(r.message);
        return;
      }
      setLaunch({
        editor: r.value.editor,
        editorUrl: r.value.editor_url,
        // The server also offers ready-made auto-submit HTML for
        // ``iframe.srcdoc``, but a srcdoc document runs with *this*
        // app's origin, and with ``allow-scripts allow-same-origin`` it
        // could script the parent. The form is built here instead and
        // posted into the iframe by name.
        formPost: Boolean(r.value.editor_html),
        accessToken: r.value.access_token,
        expiresAtMs: r.value.access_token_expires_at_ms,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [fileId]);

  // OnlyOffice: post the launch form into the iframe once per launch.
  const formPostUrl = launch?.formPost ? launch.editorUrl : null;
  useEffect(() => {
    if (formPostUrl === null || submittedForRef.current === formPostUrl) return;
    submittedForRef.current = formPostUrl;
    formRef.current?.submit();
  }, [formPostUrl]);

  // Listen for editor → host postMessages. The shape is
  // ``{MessageId, SendTime, Values}`` JSON-stringified in
  // ``event.data``. We care about exactly one event right now:
  // ``App_LoadingStatus: Document_Loaded``, which is the editor's
  // signal that it's ready to accept host messages. We respond
  // with ``Host_PostmessageReady`` (the matching docs-required
  // ack), at which point the editor unblocks subsequent host
  // postMessages. Other event types (Action_Save_Resp, etc.)
  // will land here when later quirks need them; for now we
  // ignore.
  useEffect(() => {
    if (launch === null) return;
    const editorOrigin = editorOriginOf(launch.editorUrl);

    const handleMessage = (event: MessageEvent): void => {
      // Origin pinning — only trust messages from the editor we
      // launched. Browser-authoritative; can't be spoofed by a
      // cross-origin page in another tab. No parseable editor
      // origin means we can't tell the editor from anyone else:
      // reject everything rather than trusting everything.
      if (editorOrigin === null || event.origin !== editorOrigin) return;

      let data: unknown = event.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          // Unparseable string — log for visibility but don't
          // act. Some editor channels (telemetry, debug) emit
          // non-JSON; the parse failure isn't necessarily wrong.
          // Structure only, never the payload (defence in depth: editor
          // messages shouldn't carry secrets, but don't echo them).
          console.info(
            "[wopi:recv]",
            "unparseable",
            "from",
            event.origin,
            "length=",
            String(event.data).length,
          );
          return;
        }
      }
      if (typeof data !== "object" || data === null) return;
      const msg = data as { MessageId?: unknown; Values?: unknown };

      // Diagnostic log scoped by source (when available). Filters
      // out noise from browser extensions / other postMessage
      // sources that happen to share the editor's origin; in
      // tests where ``event.source`` is null (synthetic events
      // don't carry a source), we still log so the test can
      // assert on the diagnostic surface.
      const ourSource = iframeRef.current?.contentWindow ?? null;
      const sourceMatches =
        event.source === null ||
        ourSource === null ||
        event.source === ourSource;
      if (sourceMatches) {
        console.info(
          "[wopi:recv]",
          msg.MessageId,
          "from",
          event.origin,
          "ts=",
          Date.now(),
        );
      }

      if (msg.MessageId !== "App_LoadingStatus") return;
      const values =
        typeof msg.Values === "object" && msg.Values !== null
          ? (msg.Values as { Status?: unknown })
          : null;
      if (values?.Status !== "Document_Loaded") return;
      // Editor is ready. Acknowledge per the documented
      // handshake (no Values needed — the MessageId itself is
      // the ack).
      postEditorMessage(iframeRef.current, editorOrigin, {
        MessageId: "Host_PostmessageReady",
      });
      editorReadyRef.current = true;
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [launch, triggerRefresh]);

  // Proactive timer at 50% of remaining TTL. With the 1-hour
  // default this fires at 30 min, which is the only refresh path
  // — see REFRESH_FRACTION in src/lib/wopi.ts for why we don't react to
  // editor-emitted idle signals.
  //
  // When ``triggerRefresh`` updates the launch state the effect
  // cleanup cancels the pending timer; the new effect schedules
  // the next one against the fresh TTL, and the cycle continues
  // for the lifetime of this route.
  useEffect(() => {
    if (launch === null) return;
    const remainingMs = Math.max(0, launch.expiresAtMs - Date.now());
    const delay = Math.floor(remainingMs * getRefreshFraction());
    const timer = setTimeout(() => {
      void triggerRefresh("scheduled");
    }, delay);
    return () => clearTimeout(timer);
  }, [launch, triggerRefresh]);

  if (error !== null) {
    return (
      <div className="text-sm text-destructive" role="alert">
        {t("edit.launch_failed", "Could not open the editor: {{message}}", {
          message: error,
        })}
      </div>
    );
  }
  if (launch === null) {
    return (
      <div className="text-sm text-muted-foreground" role="status">
        {t("edit.loading", "Loading editor…")}
      </div>
    );
  }
  return (
    <>
      {launch.formPost && (
        // OnlyOffice FormPost (WOPI Quirk #8): POST the token into the
        // named iframe, which then navigates to the editor's own
        // (cross-origin) page. Submitted once per launch by the effect
        // above; later token refreshes only update the value.
        <form
          ref={formRef}
          method="POST"
          action={launch.editorUrl}
          target={EDITOR_FRAME_NAME}
          hidden
        >
          <input
            type="hidden"
            name="access_token"
            value={launch.accessToken}
            readOnly
          />
          <input
            type="hidden"
            name="access_token_ttl"
            value={String(launch.expiresAtMs)}
            readOnly
          />
        </form>
      )}
      <iframe
        ref={iframeRef}
        name={EDITOR_FRAME_NAME}
        // Collabora navigates here directly; OnlyOffice is loaded by the
        // form above.
        src={launch.formPost ? undefined : launch.editorUrl}
        title={t("edit.iframe_title", "Document editor")}
        className="w-full h-[calc(100vh-8rem)] border-0"
        // Editor needs same-origin scripts within its own origin +
        // forms (for upload-style PutFile bodies in some editor
        // versions, AND for OnlyOffice's FormPost auto-submit) +
        // popups (Collabora's "Save As" submenu opens a chooser).
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        data-testid="wopi-editor-iframe"
      />
    </>
  );
}
